// The request form, once: /requisitions/new and the detail page's Edit both
// render it. It owns the typing; the page owns the saving.

import React, { useEffect, useRef, useState } from 'react';
import SegTrack from '../components/ui/SegTrack';
import Select from '../components/ui/Select';
import { Field, TextArea } from '../components/ui/Field';
import { businessToday } from '../utils/date-utils';
import EventField from './EventField';
import { IconButton, buttonClass } from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { Panel } from '../components/ui/Panel';
import { Tooltip } from '../components/ui/Tooltip';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import {
  centsToInput,
  dollarsToCents,
  formatCents,
  formatQuantity,
  isWebLink,
  normalizeLink,
  useRequisitionsApi,
  type DraftInput,
  type DraftLineInput,
  type PurchaseClassOption,
  type RequisitionDetail,
  type Urgency,
} from './requisitionsApi';

interface LineDraft {
  key: string;
  description: string;
  quantity: string;
  link_url: string;
  store: string;
  cost: string;
  /** Not edited here; carried through so an edit does not wipe it. */
  notes: string | null;
}

interface LineErrors {
  description?: string;
  quantity?: string;
  link_url?: string;
  cost?: string;
}

interface Errors {
  purpose?: string;
  lines: Record<string, LineErrors>;
  form?: string;
}

const NO_ERRORS: Errors = { lines: {} };

let lineSeq = 0;
const newKey = () => `line-${++lineSeq}`;

const blankLine = (): LineDraft => ({
  key: newKey(),
  description: '',
  quantity: '1',
  link_url: '',
  store: '',
  cost: '',
  notes: null,
});

const isBlank = (l: LineDraft) =>
  !l.description.trim() && !l.link_url.trim() && !l.store.trim() && !l.cost.trim();

const LABEL = 'mb-1 block text-xs font-medium text-on-surface-variant';

const URGENCY_OPTIONS = [
  { key: 'low', label: 'Low' },
  { key: 'normal', label: 'Normal' },
  { key: 'urgent', label: 'Urgent' },
];

export type SaveIntent = 'draft' | 'send';

interface Props {
  initial?: RequisitionDetail;
  /** Which button is working, or null. */
  saving: SaveIntent | null;
  onSave: (draft: DraftInput, intent: SaveIntent) => void;
  /** Rendered as a text-tier button at the left of the action bar. */
  onCancel?: () => void;
  cancelLabel?: string;
  /** Editing a request that is waiting for approval sends it back to draft. */
  wasPending?: boolean;
}

function fromDetail(r: RequisitionDetail): LineDraft[] {
  const lines = [...r.lines]
    .sort((a, b) => a.line_number - b.line_number)
    .map((l) => ({
      key: newKey(),
      description: l.description,
      quantity: formatQuantity(l.quantity),
      link_url: l.link_url || '',
      store: l.store || '',
      cost: centsToInput(l.estimated_unit_cost_cents),
      notes: l.notes,
    }));
  return lines.length ? lines : [blankLine()];
}

const RequisitionForm: React.FC<Props> = ({
  initial,
  saving,
  onSave,
  onCancel,
  cancelLabel = 'Cancel',
  wasPending = false,
}) => {
  const reqApi = useRequisitionsApi();
  const [purpose, setPurpose] = useState(initial?.purpose || '');
  const [neededBy, setNeededBy] = useState(initial?.needed_by || '');
  const [urgency, setUrgency] = useState<Urgency>(initial?.urgency || 'normal');
  const [classId, setClassId] = useState(
    initial?.purchase_class ? String(initial.purchase_class.id) : ''
  );
  const [event, setEvent] = useState<{ id: string; name: string } | null>(initial?.event || null);
  const [lines, setLines] = useState<LineDraft[]>(() =>
    initial ? fromDetail(initial) : [blankLine()]
  );
  const [errors, setErrors] = useState<Errors>(NO_ERRORS);
  const [classes, setClasses] = useState<PurchaseClassOption[] | null>(null);
  const [classesFailed, setClassesFailed] = useState(false);

  const descRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const focusNext = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    reqApi
      .purchaseClasses()
      .then((list) => alive && setClasses(list))
      .catch(() => alive && setClassesFailed(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A line added from the keyboard takes focus once it exists.
  useEffect(() => {
    if (!focusNext.current) return;
    descRefs.current.get(focusNext.current)?.focus();
    focusNext.current = null;
  }, [lines]);

  const updateLine = (key: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    setErrors((prev) => {
      if (!prev.lines[key]) return prev;
      const next = { ...prev.lines };
      const fieldErrors = { ...next[key] };
      Object.keys(patch).forEach((k) => {
        delete fieldErrors[k as keyof LineErrors];
      });
      next[key] = fieldErrors;
      return { ...prev, lines: next, form: undefined };
    });
  };

  const addLine = (focus = true) => {
    const line = blankLine();
    if (focus) focusNext.current = line.key;
    setLines((prev) => [...prev, line]);
  };

  const removeLine = (key: string) => {
    setLines((prev) => {
      const next = prev.filter((l) => l.key !== key);
      return next.length ? next : [blankLine()];
    });
  };

  // Enter in an item's field moves to the next item, adding one at the end,
  // so a list of five things is typed without reaching for the mouse.
  const onLineKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    e.preventDefault();
    const next = lines[index + 1];
    if (next) descRefs.current.get(next.key)?.focus();
    else if (!isBlank(lines[index])) addLine(true);
  };

  const estimate = lines.reduce(
    (acc, l) => {
      const cents = dollarsToCents(l.cost);
      const qty = Number(l.quantity);
      if (typeof cents === 'number' && Number.isFinite(qty) && qty > 0) {
        acc.total += cents * qty;
        acc.priced += 1;
      }
      if (!isBlank(l)) acc.items += 1;
      return acc;
    },
    { total: 0, priced: 0, items: 0 }
  );

  const validate = (intent: SaveIntent): { draft: DraftInput | null; errors: Errors } => {
    const next: Errors = { lines: {} };
    if (!purpose.trim()) next.purpose = 'Say what this is for.';

    const out: DraftLineInput[] = [];
    lines.forEach((l) => {
      if (isBlank(l)) return;
      const le: LineErrors = {};
      if (!l.description.trim()) le.description = 'Say what the item is.';
      const qty = Number(l.quantity);
      if (!l.quantity.trim() || !Number.isFinite(qty) || qty <= 0) le.quantity = 'More than 0.';
      const link = normalizeLink(l.link_url);
      if (link && !isWebLink(link)) le.link_url = 'Paste a web address, starting with https://';
      const cents = dollarsToCents(l.cost);
      if (cents === null) le.cost = 'A dollar amount, like 12.50';
      if (Object.keys(le).length) {
        next.lines[l.key] = le;
        return;
      }
      out.push({
        description: l.description.trim(),
        quantity: qty,
        link_url: link || null,
        store: l.store.trim() || null,
        estimated_unit_cost_cents: typeof cents === 'number' ? cents : null,
        notes: l.notes,
      });
    });

    if (intent === 'send' && out.length === 0 && Object.keys(next.lines).length === 0) {
      next.form = 'Add at least one item before sending it for approval.';
    }

    const failed = !!next.purpose || !!next.form || Object.keys(next.lines).length > 0;
    if (failed) return { draft: null, errors: next };
    return {
      draft: {
        purpose: purpose.trim(),
        needed_by: neededBy || null,
        urgency,
        purchase_class_id: classId ? Number(classId) : null,
        event_id: event ? event.id : null,
        lines: out,
      },
      errors: NO_ERRORS,
    };
  };

  const handle = (intent: SaveIntent) => {
    const { draft, errors: errs } = validate(intent);
    setErrors(errs);
    if (!draft) return;
    onSave(draft, intent);
  };

  const errorCount =
    (errors.purpose ? 1 : 0) +
    (errors.form ? 1 : 0) +
    Object.values(errors.lines).reduce((a, le) => a + Object.keys(le).length, 0);

  const busy = saving !== null;
  const today = businessToday().format('YYYY-MM-DD');

  return (
    <form
      noValidate
      // No implicit submit: Enter in the date field must not send a request
      // for approval. Both actions are buttons with their own handlers.
      onSubmit={(e) => e.preventDefault()}
      className="space-y-4"
    >
      <Reveal show={!!wasPending}>
        <Alert tone="warning" live={false}>
          This request is waiting for approval. Saving a change sends it back to draft and
          withdraws the approval request, so send it again when you are done.
        </Alert>
      </Reveal>

      <Panel title="What do you need?">
       <div className="space-y-4">
        <label className="block">
          <span className={LABEL}>What is it for?</span>
          <TextArea
            value={purpose}
            rows={2}
            maxLength={500}
            onChange={(e) => {
              setPurpose(e.target.value);
              setErrors((p) => ({ ...p, purpose: undefined }));
            }}
            placeholder="A shelf for the charging bench"
            error={!!errors.purpose}
            aria-describedby={errors.purpose ? 'req-purpose-error' : undefined}
            autoFocus={!initial}
          />
          <Reveal show={!!errors.purpose}>
            <span id="req-purpose-error" className="mt-1 block text-xs text-red-300">
              {errors.purpose}
            </span>
          </Reveal>
        </label>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="block">
            <span className={LABEL}>Needed by</span>
            <Field
              type="date"
              value={neededBy}
              min={initial ? undefined : today}
              onChange={(e) => setNeededBy(e.target.value)}
            />
          </label>
          <div>
            <span aria-hidden="true" className={LABEL}>
              How urgent?
            </span>
            <SegTrack
              label="How urgent?"
              options={URGENCY_OPTIONS}
              value={urgency}
              onChange={(k) => setUrgency(k as Urgency)}
              size="sm"
              wrap
            />
          </div>
          <div className="block">
            <span className={LABEL}>Bought for</span>
            <Select
              label="Bought for"
              value={classId}
              onChange={setClassId}
              disabled={classes === null && !classesFailed}
              options={[
                { key: '', label: classes === null && !classesFailed ? 'Loading…' : 'Not sure' },
                ...(classes || []).map((c) => ({ key: String(c.id), label: c.label })),
              ]}
            />
            <Reveal show={!!classesFailed}>
              <span className="mt-1 block text-xs text-on-surface-variant">
                Could not load the list. Leave it as Not sure, or reload the page.
              </span>
            </Reveal>
          </div>
        </div>

        <EventField
          label="For a show? (optional)"
          labelClassName={LABEL}
          value={event}
          date={neededBy || today}
          onChange={setEvent}
        />
       </div>
      </Panel>

      <Panel
        title="Items"
        actions={<span className="text-xs font-normal text-on-surface-variant">Press Enter to move to the next item</span>}
      >
       <div className="space-y-3">

        {/* Column names once, on a wide screen. Each field keeps its own label
            for a screen reader and for the stacked phone layout. */}
        <div
          aria-hidden="true"
          className="hidden gap-2 px-1 text-xs font-medium text-on-surface-variant sm:grid sm:grid-cols-[minmax(0,2.2fr)_4.5rem_minmax(0,1.6fr)_minmax(0,1fr)_6.5rem_2.25rem]"
        >
          <span>Item</span>
          <span>Qty</span>
          <span>Link</span>
          <span>Store</span>
          <span>Each (about)</span>
          <span />
        </div>

        <ul className="space-y-3 sm:space-y-2">
          {lines.map((l, i) => {
            const le = errors.lines[l.key] || {};
            const n = i + 1;
            return (
              <li
                key={l.key}
                className="grid grid-flow-row-dense grid-cols-2 gap-2 rounded-md bg-surface-container p-3 sm:grid-flow-row sm:grid-cols-[minmax(0,2.2fr)_4.5rem_minmax(0,1.6fr)_minmax(0,1fr)_6.5rem_2.25rem] sm:items-start sm:bg-transparent sm:p-0"
              >
                <label className="col-span-2 block sm:col-span-1">
                  <span className={`${LABEL} sm:sr-only`}>Item {n}</span>
                  <Field
                    ref={(el) => {
                      if (el) descRefs.current.set(l.key, el);
                      else descRefs.current.delete(l.key);
                    }}
                    type="text"
                    value={l.description}
                    onChange={(e) => updateLine(l.key, { description: e.target.value })}
                    onKeyDown={(e) => onLineKeyDown(e, i)}
                    placeholder="Wire shelf, 36 inch"
                    error={!!le.description}
                  />
                  <Reveal show={!!le.description}><span className="mt-1 block text-xs text-red-300">{le.description}</span></Reveal>
                </label>
                <label className="block">
                  <span className={`${LABEL} sm:sr-only`}>Quantity for item {n}</span>
                  <Field
                    type="text"
                    inputMode="decimal"
                    value={l.quantity}
                    onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                    onKeyDown={(e) => onLineKeyDown(e, i)}
                    error={!!le.quantity}
                    className="tabular-nums"
                  />
                  <Reveal show={!!le.quantity}><span className="mt-1 block text-xs text-red-300">{le.quantity}</span></Reveal>
                </label>
                <label className="col-span-2 block sm:col-span-1">
                  <span className={`${LABEL} sm:sr-only`}>Link for item {n}</span>
                  <Field
                    type="text"
                    inputMode="url"
                    value={l.link_url}
                    onChange={(e) => updateLine(l.key, { link_url: e.target.value })}
                    onBlur={(e) => {
                      const v = normalizeLink(e.target.value);
                      if (v !== l.link_url) updateLine(l.key, { link_url: v });
                    }}
                    onKeyDown={(e) => onLineKeyDown(e, i)}
                    placeholder="amazon.com/..."
                    error={!!le.link_url}
                    className="truncate"
                  />
                  <Reveal show={!!le.link_url}><span className="mt-1 block text-xs text-red-300">{le.link_url}</span></Reveal>
                </label>
                <label className="block">
                  <span className={`${LABEL} sm:sr-only`}>Store for item {n}</span>
                  <Field
                    type="text"
                    value={l.store}
                    onChange={(e) => updateLine(l.key, { store: e.target.value })}
                    onKeyDown={(e) => onLineKeyDown(e, i)}
                    placeholder="Home Depot"
                  />
                </label>
                <label className="block">
                  <span className={`${LABEL} sm:sr-only`}>Cost each for item {n}, in dollars</span>
                  <span className="relative block">
                    <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-on-surface-variant">
                      $
                    </span>
                    <Field
                      type="text"
                      inputMode="decimal"
                      value={l.cost}
                      onChange={(e) => updateLine(l.key, { cost: e.target.value })}
                      onKeyDown={(e) => onLineKeyDown(e, i)}
                      placeholder="0.00"
                      error={!!le.cost}
                      className="pl-6 text-right tabular-nums"
                    />
                  </span>
                  <Reveal show={!!le.cost}><span className="mt-1 block text-xs text-red-300">{le.cost}</span></Reveal>
                </label>
                <span className="flex items-end justify-end self-end sm:items-start sm:self-start">
                  <Tooltip label={`Remove item ${n}`}>
                    <IconButton onClick={() => removeLine(l.key)} label={`Remove item ${n}`}>
                      <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-4 w-4">
                        <path
                          fill="currentColor"
                          d="M6.5 1.75A.75.75 0 0 1 7.25 1h1.5a.75.75 0 0 1 .75.75V2.5h3.75a.75.75 0 0 1 0 1.5h-.6l-.62 9.06A1.75 1.75 0 0 1 10.28 14.7H5.72a1.75 1.75 0 0 1-1.75-1.64L3.35 4h-.6a.75.75 0 0 1 0-1.5H6.5v-.75ZM4.85 4l.62 8.96a.25.25 0 0 0 .25.24h4.56a.25.25 0 0 0 .25-.24L11.15 4h-6.3Z"
                        />
                      </svg>
                    </IconButton>
                  </Tooltip>
                </span>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          onClick={() => addLine(true)}
          className={buttonClass('quiet')}
        >
          <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5">
            <path fill="currentColor" d="M8 2.5a.75.75 0 0 1 .75.75v4h4a.75.75 0 0 1 0 1.5h-4v4a.75.75 0 0 1-1.5 0v-4h-4a.75.75 0 0 1 0-1.5h4v-4A.75.75 0 0 1 8 2.5Z" />
          </svg>
          Add item
        </button>
       </div>
      </Panel>

      {/* The document commits after its content, so its actions sit at the
          bottom beside the running estimate. */}
      <Panel label="Save or send" on="panel" padding="sm" className="sticky bottom-0 z-10 !rounded-b-none px-4 shadow-overlay sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 text-sm text-on-surface-variant" role="status" aria-live="polite">
            <FadeIn key={errorCount > 0 ? 'errors' : 'estimate'}>
            {errorCount > 0 ? (
              <span className="text-red-300">
                {errors.form
                  ? errors.form
                  : `${errorCount} ${errorCount === 1 ? 'field needs' : 'fields need'} a fix before saving.`}
              </span>
            ) : (
              <>
                <span className="text-on-surface tabular-nums">
                  {estimate.items} {estimate.items === 1 ? 'item' : 'items'}
                </span>
                {estimate.priced > 0 && (
                  <>
                    , about <span className="text-on-surface tabular-nums">{formatCents(estimate.total)}</span>
                  </>
                )}
              </>
            )}
            </FadeIn>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className={buttonClass('quiet')}
              >
                {cancelLabel}
              </button>
            )}
            <button
              type="button"
              onClick={() => handle('draft')}
              disabled={busy}
              className={buttonClass('quiet')}
            >
              {saving === 'draft' ? 'Saving…' : 'Save draft'}
            </button>
            <button
              type="button"
              onClick={() => handle('send')}
              disabled={busy}
              className={buttonClass('filled')}
            >
              {saving === 'send' ? 'Sending…' : 'Send for approval'}
            </button>
          </div>
        </div>
      </Panel>
    </form>
  );
};

export default RequisitionForm;
